import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import ReviewCollection from './ReviewCollection.jsx';
import ReviewSingleFeature from './ReviewSingleFeature.jsx';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {FEATURE_STATE} from '@app/constants/uploads.js';
import {useFinishUpload, useGetUploadReviewList} from '@api/asyncIngest';

const ReviewUploadDialog = ({upload, open, onClose}) => {
  const {data: features, error, isPending} = useGetUploadReviewList(upload);

  const {mutate: acceptAll} = useFinishUpload(
    {upload, acceptAll: true},
    {onSuccess: onClose}
  );
  const {mutate: finishUpload} = useFinishUpload(
    {upload, acceptAll: false},
    {onSuccess: onClose}
  );

  // reviewNotRequired indicates that the upload has no errors/alternates
  // and can be completed without review
  const reviewNotRequired = useMemo(() => {
    const reviewFeatures = features?.to_review;

    // make sure `to_review` is populated.
    if (!reviewFeatures) {
      return true;
    }

    return features?.to_review?.length === 0;
  }, [features]);

  const uploadContainsOnlyErrors =
    upload.total_errors > 0 && upload.total_features === upload.total_errors;

  // reviewComplete indicates that there were features to review,
  // but they've all been accepted or discarded by the user
  const reviewComplete = useMemo(() => {
    if (!features?.to_review) {
      return false;
    }

    const reviewFeatures = features.to_review;

    if (reviewFeatures.length === 0) {
      return true;
    }

    const remaining = reviewFeatures.filter(
      feature => feature.state === FEATURE_STATE.ALTERNATE
    );

    return remaining.length === 0;
  }, [features]);

  const renderContent = () => {
    if (isPending) {
      return <Typography>Loading...</Typography>;
    }

    if (error) {
      return <Typography>Error: {error.message}</Typography>;
    }

    return (
      <Stack gap={1}>
        {reviewNotRequired ? (
          <ReviewCollection upload={upload} />
        ) : (
          <ReviewSingleFeature
            upload={upload}
            features={features.to_review}
            onClose={onClose}
          />
        )}
      </Stack>
    );
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>Review Upload</DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions sx={{justifyContent: 'normal'}}>
        <Stack direction="row" sx={{flex: 1}}>
          {!error &&
            (!!features && reviewNotRequired ? (
              <Alert severity="success">
                Upload successful. Use the Finish Upload button to create a
                Collection.
              </Alert>
            ) : uploadContainsOnlyErrors ? (
              <Alert severity="error">
                All uploaded features contain an error. Please correct errors
                and re-upload.
              </Alert>
            ) : reviewComplete ? (
              <Alert severity="success">All features have been reviewed</Alert>
            ) : (
              <Alert severity="warning">
                {features?.to_review?.length} features requiring review
              </Alert>
            ))}
          <Button
            onClick={finishUpload}
            disabled={!features || !reviewComplete || uploadContainsOnlyErrors}
            color="primary"
            variant="contained"
            sx={{ml: 'auto'}}
          >
            Finish upload
          </Button>
          {!!features && !reviewComplete && !uploadContainsOnlyErrors && (
            <Button onClick={acceptAll} color="secondary" sx={{ml: 1}}>
              Accept all
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

ReviewUploadDialog.propTypes = {
  upload: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default ReviewUploadDialog;
